import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Icon from '../../../../shared/_TBComponents/Icon';
import Text from '../../../../shared/_TBComponents/Text';
import ShimmerWorkoutsListItem from '../../../workouts/components/presentational/ShimmerWorkoutsListItem';
import StandardWorkoutListItem from '../../../workouts/components/presentational/StandardWorkoutListItem';

const ReadOnlyWrapper = styled.div`
  pointer-events: none !important;
  cursor: default !important;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  & * {
    cursor: default !important;
  }
`;

const ExpandedContent = styled.div`
  margin-top: 12px;
`;

const ArrowIconWrapper = styled.span`
  display: flex;
  align-items: center;
  margin-left: 8px;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${(props) => (props.isExpanded ? 90 : 0)}deg);
`;

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
`;

const ProgramCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid #EEE;
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  cursor: default;
  margin-bottom: 8px;
`;

const ProgramHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const ProgramInfoColumn = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
`;

const ProgramInfoButton = styled.div`
  display: flex;
  flex-direction: column;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
`;

const StatusAndText = styled.div`
  display: flex;
  align-items: center;
  height: 21px;
`;

const ProgramStatusCircle = styled.div`
  border-radius: 50%;
  background: ${(props) => props.color};
  border: ${(props) => (!props.started ? '1px solid grey' : `1px solid ${props.color}`)};
  width: 16px;
  height: 16px;
  margin-right: 6px;
`;

const PreviewProgramList = ({
  activePrograms,
  expandedProgramKey,
  setExpandedProgramKey,
  programItems,
  currentUser,
  selectedAthlete,
  dispatch,
  fetchProgramItems,
  loadingContentOffset,
}) => {
  if (!Array.isArray(activePrograms) || activePrograms.length === 0) {
    return (
      <Text
        styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}
        styledWrapperProps={{ margin: '6px 0 12px 12px' }}
      >
        No current active programs
      </Text>
    );
  }

  const colorCoder = (status) => {
    if (status === 'In Progress') {
      return '#FFCB47';
    }
    if (status === 'Completed') {
      return '#0dcc8a';
    }
    if (status === 'Skipped') {
      return '#ff0024';
    }
    if (status === 'Not Started') {
      return '';
    }
    return '';
  };

  return (
    <MainContainer>
      {activePrograms.map((program) => {
        const {
          saveInfoTitle,
          name,
          programName,
          title,
          instanceStatus,
          status: statusRaw,
          dayBasedInstanceId,
          dayBasedProgramId,
          trueDayNum,
        } = program;

        const displayName = saveInfoTitle || name || programName || title || 'Program';
        const status = instanceStatus || statusRaw;
        const key = dayBasedInstanceId || `${dayBasedProgramId || 'program'}-${trueDayNum || 'day'}`;
        const isExpanded = expandedProgramKey === key;
        const canExpand = currentUser?.accountCode
          && selectedAthlete?.id && dayBasedProgramId && trueDayNum;

        const handleClick = () => {
          if (!canExpand) return;
          if (isExpanded) {
            setExpandedProgramKey(null);
          } else {
            setExpandedProgramKey(key);
            dispatch(fetchProgramItems(
              currentUser.accountCode,
              selectedAthlete.id,
              dayBasedProgramId,
              trueDayNum,
            ));
          }
        };

        const isCurrentProgramDay = Boolean(
          programItems
          && programItems.dayBasedProgramId === dayBasedProgramId
          && programItems.trueDayNum === trueDayNum,
        );

        const programWorkouts = isCurrentProgramDay ? programItems?.workoutItems || [] : [];

        return (
          <ProgramCard key={key}>
            <ProgramInfoButton
              role='button'
              tabIndex={0}
              disabled={!canExpand}
              onClick={handleClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick();
                }
              }}
              aria-expanded={isExpanded}
              aria-label={`Toggle program ${displayName}`}
            >
              <ProgramHeader>
                <ProgramInfoColumn>
                  <Text styledTextProps={{ fontSize: '16px', fontWeight: 700 }}>
                    {displayName}
                  </Text>
                  <StatusAndText>
                    <ProgramStatusCircle started={status !== 'Not Started'} color={colorCoder(status)} />
                    <Text styledTextProps={{ fontSize: '16px', fontWeight: 400, color: '#9E9E9E' }}>
                      {status}
                    </Text>
                  </StatusAndText>
                </ProgramInfoColumn>
                <ArrowIconWrapper isExpanded={isExpanded} aria-hidden='true'>
                  <Icon icon='altrightarrow' size={18} color='#929292' />
                </ArrowIconWrapper>
              </ProgramHeader>
            </ProgramInfoButton>
            {isExpanded && (
              <ExpandedContent>
                <ReadOnlyWrapper style={{ marginLeft: '-20px' }}>
                  {!isCurrentProgramDay ? (
                    <div style={loadingContentOffset ? { marginLeft: `${loadingContentOffset}px` } : undefined}>
                      {[1, 2, 3].map((idx) => (
                        <ShimmerWorkoutsListItem key={idx} showVerticalLine={false} index={idx} />
                      ))}
                    </div>
                  ) : (
                    programWorkouts.length > 0 && programWorkouts.map((item, idx, arr) => {
                      const nextItem = arr[idx + 1];
                      const showVerticalLine = Boolean(nextItem) && nextItem?.type !== 'BR';
                      return (
                        <StandardWorkoutListItem
                          key={item.saveDataId || item.assignedId || idx}
                          workoutItem={item}
                          index={idx}
                          showVerticalLine={showVerticalLine}
                        />
                      );
                    })
                  )}
                </ReadOnlyWrapper>
              </ExpandedContent>
            )}
          </ProgramCard>
        );
      })}
    </MainContainer>
  );
};

PreviewProgramList.propTypes = {
  activePrograms: PropTypes.arrayOf(PropTypes.shape({
    saveInfoTitle: PropTypes.string,
    name: PropTypes.string,
    programName: PropTypes.string,
    title: PropTypes.string,
    instanceStatus: PropTypes.string,
    status: PropTypes.string,
    dayBasedInstanceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dayBasedProgramId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dayNum: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })),
  expandedProgramKey: PropTypes.string,
  setExpandedProgramKey: PropTypes.func,
  programItems: PropTypes.shape({
    dayBasedProgramId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trueDayNum: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    weekTitle: PropTypes.string,
    dayTitle: PropTypes.string,
    workoutItems: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  currentUser: PropTypes.shape({
    accountCode: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  selectedAthlete: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  dispatch: PropTypes.func,
  fetchProgramItems: PropTypes.func,
  loadingContentOffset: PropTypes.number,
};

PreviewProgramList.defaultProps = {
  activePrograms: [],
  expandedProgramKey: null,
  setExpandedProgramKey: () => {},
  programItems: null,
  currentUser: {},
  selectedAthlete: {},
  dispatch: () => {},
  fetchProgramItems: () => {},
  loadingContentOffset: 0,
};

export default PreviewProgramList;
